import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Toolbar,
  AppBar,
  Button,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Stack
} from '@mui/material';
import { TreeItem } from '@mui/x-tree-view';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Folder as FolderIcon,
  Description as DocumentIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import AddDocumentDialog from '../documents/AddDocumentDialog';
import axios from '../../config/axios';
import Mermaid from '../Mermaid';
import mermaid from 'mermaid';
import { Canvg } from 'canvg';
import { sanitizeMermaidSvg } from '../../utils/sanitizeMermaidSvg';
import { unifiedDocumentList } from '../../pages/DocumentMap'; // Adjust path if needed
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api`;

// Ensure Mermaid is available globally and initialized
if (typeof window !== 'undefined' && !window.mermaidInitialized) {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'base',
    themeVariables: {
      fontFamily: 'Arial, sans-serif'
    },
    securityLevel: 'strict',
    htmlLabels: false,
    flowchart: { htmlLabels: false }
  });
  window.mermaid = mermaid;
  window.mermaidInitialized = true;
}

const documentHierarchy = {
  id: 'root',
  name: 'Trial Master File',
  children: [
    {
      id: '1',
      name: 'Trial Oversight',
      status: 'Complete',
      children: [
        {
          id: '1.1',
          name: 'Protocol and Amendments',
          status: 'In Progress',
          documents: [
            { id: 'd1', name: 'Protocol v1.0', date: '2024-01-15', status: 'Approved' },
            { id: 'd2', name: 'Protocol v2.0', date: '2024-02-20', status: 'Draft' },
          ]
        },
        {
          id: '1.2',
          name: 'Informed Consent Forms',
          status: 'Complete',
          documents: [
            { id: 'd3', name: 'ICF Template', date: '2024-01-10', status: 'Approved' },
          ]
        }
      ]
    },
    {
      id: '2',
      name: 'Regulatory Documentation',
      status: 'In Progress',
      children: [
        {
          id: '2.1',
          name: 'IRB/IEC Communications',
          status: 'Pending',
          documents: [
            { id: 'd4', name: 'IRB Approval', date: '2024-02-01', status: 'Pending' },
          ]
        },
        {
          id: '2.2',
          name: 'Regulatory Submissions',
          status: 'In Progress',
          documents: [
            { id: 'd5', name: 'FDA Form 1572', date: '2024-01-25', status: 'In Review' },
          ]
        }
      ]
    },
    {
      id: '3',
      name: 'Site Documentation',
      status: 'Not Started',
      children: [
        {
          id: '3.1',
          name: 'Site Selection',
          status: 'Not Started',
          documents: []
        },
        {
          id: '3.2',
          name: 'Site Monitoring',
          status: 'Not Started',
          documents: []
        }
      ]
    }
  ]
};

const FlowChartCanvas = ({ width = 800, height = 500 }) => {
  const canvasRef = useRef(null);

  const drawBox = (ctx, x, y, width, height, text, style = 'default') => {
    ctx.save();
    
    // Box fill color based on style
    ctx.fillStyle = style === 'highlighted' ? '#fff3e0' : 
                   style === 'special' ? '#e3f2fd' : '#ffffff';
    
    // Draw rounded rectangle with shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;
    
    // Path for rounded rectangle
    ctx.beginPath();
    const radius = 8;
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    
    // Fill and stroke
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = style === 'highlighted' ? '#ffb74d' : 
                     style === 'special' ? '#64b5f6' : '#90a4ae';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    
    // Text
    ctx.fillStyle = '#37474f';
    ctx.font = '13px Roboto, Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Handle text wrapping
    const words = text.split(' ');
    let line = '';
    let lines = [];
    const maxWidth = width - 16;
    
    for (let word of words) {
      const testLine = line + word + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && line !== '') {
        lines.push(line);
        line = word + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line);
    
    // Draw wrapped text
    const lineHeight = 16;
    const startY = y + (height/2) - ((lines.length - 1) * lineHeight/2);
    lines.forEach((line, i) => {
      ctx.fillText(line.trim(), x + width/2, startY + (i * lineHeight));
    });
    
    ctx.restore();
  };

  const drawArrow = (ctx, fromX, fromY, toX, toY) => {
    ctx.save();
    
    // Draw path with gradient
    const gradient = ctx.createLinearGradient(fromX, fromY, toX, toY);
    gradient.addColorStop(0, '#90a4ae');
    gradient.addColorStop(1, '#607d8b');
    
    ctx.strokeStyle = gradient;
    ctx.lineWidth = 1.5;
    
    // Draw curved path
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    
    // If it's a vertical line
    if (Math.abs(fromX - toX) < 10) {
      ctx.lineTo(toX, toY);
    } else {
      // Create a curved path
      const controlPointY = (fromY + toY) / 2;
      ctx.bezierCurveTo(
        fromX, controlPointY,
        toX, controlPointY,
        toX, toY
      );
    }
    ctx.stroke();
    
    // Draw arrowhead
    const headlen = 10;
    const angle = Math.atan2(toY - fromY, toX - fromX);
    
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(
      toX - headlen * Math.cos(angle - Math.PI / 6),
      toY - headlen * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(toX, toY);
    ctx.lineTo(
      toX - headlen * Math.cos(angle + Math.PI / 6),
      toY - headlen * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
    
    ctx.restore();
  };

  const draw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);

    // Enable antialiasing
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Box dimensions
    const boxWidth = 140;
    const boxHeight = 40;
    const gap = 30;
    const centerX = width/2;

    // Core modules
    drawBox(ctx, centerX - boxWidth - gap*2, 20, boxWidth, boxHeight, 'Core Clinical module', 'highlighted');
    drawBox(ctx, centerX + gap*2, 20, boxWidth, boxHeight, 'Core Operations Module', 'highlighted');

    // Protocol Synopsis
    const synopsisY = 100;
    drawBox(ctx, centerX - boxWidth/2, synopsisY, boxWidth, boxHeight, 'Protocol Synopsis');
    drawArrow(ctx, centerX - boxWidth/2 + boxWidth/2, 60, centerX, synopsisY);

    // Protocol
    const protocolY = synopsisY + boxHeight + 40;
    drawBox(ctx, centerX - boxWidth/2, protocolY, boxWidth, boxHeight, 'Protocol');
    drawArrow(ctx, centerX, synopsisY + boxHeight, centerX, protocolY);

    // Master ICF and CRF Design
    const branchY = protocolY + boxHeight + 50;
    const leftX = centerX - boxWidth*1.5 - gap;
    const rightX = centerX + boxWidth/2 + gap;

    drawBox(ctx, leftX, branchY, boxWidth, boxHeight, 'Master ICF');
    drawBox(ctx, rightX, branchY, boxWidth, boxHeight, 'CRF Design');

    // Draw connectors from Protocol
    drawArrow(ctx, centerX - boxWidth/4, protocolY + boxHeight, leftX + boxWidth/2, branchY);
    drawArrow(ctx, centerX + boxWidth/4, protocolY + boxHeight, rightX + boxWidth/2, branchY);

    // CRF Completion Guideline
    const guidelineY = branchY + boxHeight + 50;
    drawBox(ctx, centerX - boxWidth/2, guidelineY, boxWidth, boxHeight, 'CRF Completion Guideline');
    drawArrow(ctx, rightX + boxWidth/2, branchY + boxHeight, centerX, guidelineY);

    // TMF
    const tmfY = guidelineY + boxHeight + 60;
    drawBox(ctx, centerX - boxWidth/2, tmfY, boxWidth, boxHeight, 'TMF', 'special');

    // Connect all paths to TMF
    drawArrow(ctx, leftX + boxWidth/2, branchY + boxHeight, centerX - boxWidth/4, tmfY);
    drawArrow(ctx, centerX, guidelineY + boxHeight, centerX, tmfY);
    drawArrow(ctx, rightX + boxWidth/2, branchY + boxHeight + 25, centerX + boxWidth/4, tmfY);

    // Side documents
    const sideX = centerX + boxWidth*1.5 + gap;
    drawBox(ctx, sideX, tmfY - boxHeight - gap, boxWidth, boxHeight, 'Documents from Sites', 'highlighted');
    drawBox(ctx, sideX, tmfY + gap, boxWidth, boxHeight, 'Documents from Company', 'highlighted');

    // Connect side documents
    drawArrow(ctx, centerX + boxWidth/2, tmfY, sideX, tmfY - boxHeight/2 - gap);
    drawArrow(ctx, centerX + boxWidth/2, tmfY + boxHeight/2, sideX, tmfY + gap + boxHeight/2);

    // Submission packages
    const submissionY = tmfY + boxHeight + 60;
    const sp1X = centerX - boxWidth*1.5 - gap;
    const sp3X = centerX + boxWidth/2 + gap;

    drawBox(ctx, sp1X, submissionY, boxWidth, boxHeight, 'Submission Package 1');
    drawBox(ctx, centerX - boxWidth/2, submissionY, boxWidth, boxHeight, 'Submission Package 2');
    drawBox(ctx, sp3X, submissionY, boxWidth, boxHeight, 'Submission Package 3');

    // Connect TMF to submission packages
    drawArrow(ctx, centerX, tmfY + boxHeight, sp1X + boxWidth/2, submissionY);
    drawArrow(ctx, centerX, tmfY + boxHeight, centerX, submissionY);
    drawArrow(ctx, centerX, tmfY + boxHeight, sp3X + boxWidth/2, submissionY);
  };
  // eslint-disable-next-line
  useEffect(() => {
    draw();
    // eslint-disable-next-line
  }, []);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        maxWidth: '100%',
        height: 'auto',
        display: 'block',
        margin: '0 auto',
      }}
    />
  );
};

function FlowchartBuilder({ documentMapItems, onFlowchartGenerated }) {
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [flowchart, setFlowchart] = useState(null);
  const [svgString, setSvgString] = useState(null);
  const [error, setError] = useState('');

  // Helper to check if Protocol is present in the available documents
  const protocolExists = documentMapItems.some(doc => doc.title.toLowerCase().includes('protocol'));

  // Handle selection and priority assignment
  const handleSelect = (docId, checked) => {
    setError(''); // Clear error on selection change
    if (checked) {
      setSelectedDocs([...selectedDocs, { id: docId, title: documentMapItems.find(d => d._id === docId).title, priority: selectedDocs.length + 1 }]);
    } else {
      setSelectedDocs(selectedDocs.filter(d => d.id !== docId));
    }
  };

  const handlePriorityChange = (docId, newPriority) => {
    setSelectedDocs(selectedDocs.map(d => d.id === docId ? { ...d, priority: Number(newPriority) } : d));
  };

  // Document dependencies mapping (keys are lowercase doc ids)
  const documentDependencies = {
    'subject-asset': ['protocol', 'icf'],
    'dsmb': ['protocol', 'sap'],
    'stering committee charter': ['protocol'],
    'idmc': ['protocol', 'sap'],
    'adjudication charter': ['protocol'],
    'ae-sae': ['protocol'],
    'subject-reimbursement': ['country regulations', 'protocol', 'budget'],
    'randomization': ['protocol', 'sap'],
    'sap': ['protocol'],
    'patient-diary': ['protocol'],
    'protocol-deviation': ['protocol'],
    'ip-management': ['protocol'],
    'site-training': ['protocol'],
    'site-monitoring': ['protocol', 'ae-sae'],
    'recruitment': ['protocol'],
    'retention-compliance': ['protocol'],
    'cultural-linguistic': ['protocol'],
    'translational-analysis': ['protocol'],
    'imaging-charter': ['protocol'],
    'bioanalytical': ['protocol'],
    'pk-pd': ['protocol', 'sap'],
    'icf': ['protocol'],
    'protocol': [],
  };

  // Validation before generating flowchart
  const validateSelection = () => {
    const selectedTitles = selectedDocs.map(d => d.title.toLowerCase());
    const errors = [];

    // Check each selected document's dependencies
    selectedDocs.forEach(doc => {
      const docId = doc.id.toLowerCase();
      const dependencies = documentDependencies[docId] || [];
      
      dependencies.forEach(dep => {
        const depExists = selectedTitles.some(title => 
          title.includes(dep.toLowerCase()) || 
          // Special case for compound terms
          (dep.includes('/') && dep.split('/').some(part => title.includes(part.trim().toLowerCase())))
        );
        
        if (!depExists) {
          errors.push(`${doc.title} requires ${dep}`);
        }
      });
    });

    if (errors.length > 0) {
      setError(`Missing dependencies:\n${errors.join('\n')}`);
      return false;
    }

    return true;
  };

  // Send to backend and get flowchart
  const generateFlowchart = async () => {
    setError('');
    if (!validateSelection()) return;
    try {
    const response = await axios.post(`${API_BASE_URL}/document-map/flowchart`, { documents: selectedDocs });
    setFlowchart(response.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to generate flowchart');
      // fallback logic
      const mockFlowchart = {
        nodes: selectedDocs.map(doc => ({ id: doc.id, label: doc.title })),
        edges: selectedDocs.slice(0, -1).map((doc, index) => ({
          from: doc.id,
          to: selectedDocs[index + 1].id
        }))
      };
      setFlowchart(mockFlowchart);
    }
  };

  // When the SVG is rendered, store it
  const handleExport = (svg) => setSvgString(svg);

  // When user confirms, call parent with both data and SVG
  const handleConfirm = () => {
    if (flowchart && svgString) {
      onFlowchartGenerated(flowchart, svgString);
    }
  };

  return (
    <div style={{ marginTop: 32, padding: 16, border: '1px solid #eee', borderRadius: 8 }}>
      <h3>Select Documents and Assign Priorities</h3>
      {error && <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>}
      {documentMapItems.map(doc => (
        <div key={doc._id} style={{ marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={selectedDocs.some(d => d.id === doc._id)}
            onChange={e => handleSelect(doc._id, e.target.checked)}
          />
          {doc.title}
          {selectedDocs.some(d => d.id === doc._id) && (
            <input
              type="number"
              min={1}
              value={selectedDocs.find(d => d.id === doc._id).priority}
              onChange={e => handlePriorityChange(doc._id, e.target.value)}
              style={{ width: 40, marginLeft: 8 }}
            />
          )}
        </div>
      ))}
      <button onClick={generateFlowchart} disabled={selectedDocs.length < 2} style={{ marginTop: 8 }}>
        Generate Flowchart
      </button>
      {flowchart && (
        <>
          <MermaidFlowchart nodes={flowchart.nodes} edges={flowchart.edges} onExport={handleExport} />
          <button onClick={handleConfirm} style={{ marginTop: 16 }} disabled={!svgString}>
            Save Flowchart
          </button>
        </>
      )}
    </div>
  );
}

async function svgStringToPngBlob(svgString, width, height) {
  // Debug: Log SVG string
  console.log('SVG string for PNG conversion:', svgString);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const v = await Canvg.fromString(ctx, svgString);
  await v.render();
  return new Promise((resolve) => {
    canvas.toBlob(blob => resolve(blob), 'image/png');
  });
}

function MermaidFlowchart({ nodes, edges, onExport }) {
  // Build Mermaid DSL
  // 
  

  let dsl = 'graph TD;\n';
nodes.forEach(node => {
  const cleanId = node.id.replace(/\s+/g, '_');
  dsl += `  ${cleanId}["${node.label}"];\n`;
});
edges.forEach(edge => {
  const from = edge.from.replace(/\s+/g, '_');
  const to = edge.to.replace(/\s+/g, '_');
  dsl += `  ${from} --> ${to};\n`;
});

  // Render SVG and export string
  const ref = useRef();
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      typeof document !== 'undefined' &&
      window.mermaid &&
      ref.current
    ) {
      window.mermaid.render('theGraph', dsl).then(({ svg: rawSvg }) => {
        const svg = sanitizeMermaidSvg(rawSvg);
        ref.current.innerHTML = svg;
        if (onExport) onExport(svg);
      });
    }
  }, [dsl, onExport]);

  return (
    <div style={{ marginTop: 24 }}>
      <h4>Flowchart Preview</h4>
      <div ref={ref} />
    </div>
  );
}

const DocumentMap = ({ onSubmit, initialData, loading, projectId }) => {
  // const [viewMode, setViewMode] = useState('tree');
  const [searchTerm, setSearchTerm] = useState('');
  // const [expanded, setExpanded] = useState(['root']);
  const [selectedItem, setSelectedItem] = useState(unifiedDocumentList[0]?._id || '');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [flowchart, setFlowchart] = useState(null);
  const [flowchartDialogOpen, setFlowchartDialogOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);
  const [flowchartImageUrl, setFlowchartImageUrl] = useState(null);
  const navigate = useNavigate();

  // Map unifiedDocumentList to expected format
  const mappedDocumentList = unifiedDocumentList.map(doc => ({
    ...doc,
    _id: doc.id,
    title: doc.name,
  }));

  // Fetch last flowchart PNG on page load
  useEffect(() => {
    async function fetchFlowchartImage() {
      try {
        const res = await axios.get(`${API_BASE_URL}/document-map/flowchart-image?projectId=${projectId}`, {
          responseType: 'blob'
        });
        if (res.status === 200) {
          const url = URL.createObjectURL(res.data);
          setFlowchartImageUrl(url);
        }
      } catch (err) {
        setFlowchartImageUrl(null); // No image found, fallback to default
      }
    }
    if (projectId) fetchFlowchartImage();
  }, [projectId]);

  // Callback function to receive flowchart from FlowchartBuilder
  const handleFlowchartGenerated = async (flowchartData, svgString) => {
    setFlowchart(flowchartData);
    setFlowchartDialogOpen(false);
    // Convert SVG to PNG and upload
    if (svgString && projectId) {
      const width = 500;
      const height = 350;
      const pngBlob = await svgStringToPngBlob(svgString, width, height);
      const formData = new FormData();
      formData.append('file', pngBlob, 'flowchart.png');
      formData.append('projectId', projectId);
      await axios.post(`${API_BASE_URL}/document-map/flowchart-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // Show the PNG preview immediately
      const url = URL.createObjectURL(pngBlob);
      setFlowchartImageUrl(url);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line
  }, [projectId]);

  // Handle Next button click
  const handleNextClick = () => {
    if (onSubmit) {
      // Pass the document map data to the parent component
      onSubmit({ document_map: documentHierarchy });
    }
  };

  // const handleToggle = (event, nodeIds) => {
  //   setExpanded(nodeIds);
  // };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'complete':
        return 'success';
      case 'in progress':
        return 'warning';
      case 'pending':
        return 'info';
      case 'approved':
        return 'success';
      case 'draft':
        return 'default';
      case 'in review':
        return 'warning';
      default:
        return 'default';
    }
  };
  // eslint-disable-next-line
  const renderTree = (nodes) => (
    <TreeItem 
      key={nodes.id}
      nodeId={nodes.id}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
          {nodes.documents ? <DocumentIcon color="action" sx={{ mr: 1 }} /> : <FolderIcon color="primary" sx={{ mr: 1 }} />}
          <Typography variant="body2" sx={{ mr: 2 }}>
            {nodes.name}
          </Typography>
          {nodes.status && (
            <Chip 
              label={nodes.status} 
              size="small" 
              color={getStatusColor(nodes.status)}
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>
      }
    >
      {Array.isArray(nodes.children)
        ? nodes.children.map((node) => renderTree(node))
        : null}
      {nodes.documents?.map((doc) => (
        <TreeItem
          key={doc.id}
          nodeId={doc.id}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
              <DocumentIcon color="action" sx={{ mr: 1 }} />
              <Typography variant="body2" sx={{ mr: 2 }}>
                {doc.name}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mr: 2 }}>
                {doc.date}
              </Typography>
              <Chip 
                label={doc.status} 
                size="small" 
                color={getStatusColor(doc.status)}
                sx={{ ml: 'auto' }}
              />
            </Box>
          }
        />
      ))}
    </TreeItem>
  );

  const renderComponent = (selectedItem) => {
    const selectedNav = mappedDocumentList.find(item => item._id === selectedItem);
    
    if (!selectedNav) {
      return (
        <Box sx={{ p: 3, height: 'calc(100vh - 128px)' }}>
          <Typography variant="h5">Select an item from the sidebar</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, height: 'calc(100vh - 128px)', overflow: 'auto' }}>
        <Typography variant="h5" gutterBottom>{selectedNav.title}</Typography>
        <Paper sx={{ p: 3, position: 'relative', minHeight: 'calc(100% - 48px)' }}>
          {/* Document Section */}
          {/* <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>Documents</Typography>
            <Typography variant="body1" paragraph>
              This section contains information and documents related to {selectedNav.title.toLowerCase()}.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              color="primary"
              onClick={() => setAddDialogOpen(true)}
            >
              Add Document
            </Button>
          </Box> */}
        </Paper>
      </Box>
    );
  };

  const renderReportsAndAnalytics = () => (
    <Box sx={{ p: 3, height: 'calc(100vh - 128px)', overflow: 'auto' }}>
      <Typography variant="h5" gutterBottom>Reports & Analytics</Typography>
      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Total Documents</Typography>
            <Typography variant="h3" color="primary">156</Typography>
            <Typography variant="body2" color="text.secondary">Last updated: Today</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Active Studies</Typography>
            <Typography variant="h3" color="secondary">12</Typography>
            <Typography variant="body2" color="text.secondary">Across 5 projects</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>Pending Reviews</Typography>
            <Typography variant="h3" sx={{ color: 'warning.main' }}>8</Typography>
            <Typography variant="body2" color="text.secondary">Requires attention</Typography>
          </Paper>
        </Grid>

        {/* Charts Section */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom>Document Status Distribution</Typography>
            <Box sx={{ 
              height: '300px', 
              display: 'flex', 
              alignItems: 'flex-end',
              justifyContent: 'space-around',
              pt: 4 
            }}>
              {/* Dummy Bar Chart */}
              <Box sx={{ width: '60px', height: '80%', bgcolor: 'success.light', borderRadius: 1 }} />
              <Box sx={{ width: '60px', height: '60%', bgcolor: 'warning.light', borderRadius: 1 }} />
              <Box sx={{ width: '60px', height: '40%', bgcolor: 'error.light', borderRadius: 1 }} />
              <Box sx={{ width: '60px', height: '70%', bgcolor: 'info.light', borderRadius: 1 }} />
              <Box sx={{ width: '60px', height: '50%', bgcolor: 'secondary.light', borderRadius: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
              <Typography variant="caption">Complete</Typography>
              <Typography variant="caption">In Progress</Typography>
              <Typography variant="caption">Pending</Typography>
              <Typography variant="caption">Review</Typography>
              <Typography variant="caption">Draft</Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '400px' }}>
            <Typography variant="h6" gutterBottom>Recent Activity</Typography>
            <List>
              {[
                { text: 'Protocol v2.0 updated', time: '2 hours ago' },
                { text: 'New ICF Template added', time: '4 hours ago' },
                { text: 'Site Documentation reviewed', time: '1 day ago' },
                { text: 'CRF Design approved', time: '2 days ago' },
                { text: 'TMF audit completed', time: '3 days ago' },
              ].map((item, index) => (
                <ListItem key={index} divider>
                  <ListItemText 
                    primary={item.text}
                    secondary={item.time}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSettings = () => (
    <Box sx={{ p: 3, height: 'calc(100vh - 128px)', overflow: 'auto' }}>
      <Typography variant="h5" gutterBottom>Settings</Typography>
      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>General Settings</Typography>
            <List>
              <ListItem>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText 
                  primary="Default View Mode"
                  secondary="Choose between grid or list view"
                />
                <Button variant="outlined" size="small">Change</Button>
              </ListItem>
              <ListItem>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText 
                  primary="Notifications"
                  secondary="Manage email and system notifications"
                />
                <Button variant="outlined" size="small">Configure</Button>
              </ListItem>
              <ListItem>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText 
                  primary="Language"
                  secondary="Set your preferred language"
                />
                <Button variant="outlined" size="small">Select</Button>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* User Preferences */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>User Preferences</Typography>
            <List>
              <ListItem>
                <ListItemIcon><PeopleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Profile Settings"
                  secondary="Update your personal information"
                />
                <Button variant="outlined" size="small">Edit</Button>
              </ListItem>
              <ListItem>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText 
                  primary="Theme"
                  secondary="Choose between light and dark mode"
                />
                <Button variant="outlined" size="small">Change</Button>
              </ListItem>
              <ListItem>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText 
                  primary="Accessibility"
                  secondary="Configure accessibility options"
                />
                <Button variant="outlined" size="small">Configure</Button>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* System Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>System Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Version</Typography>
                <Typography variant="body1">v1.2.0</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Last Updated</Typography>
                <Typography variant="body1">March 13, 2024</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2">Storage Used</Typography>
                <Typography variant="body1">1.2 GB of 5 GB</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderMainContent = () => {
    return (
      <Box sx={{ p: 3, height: 'calc(100vh - 128px)', overflow: 'auto' }}>
        {/* Generate Flowchart button at the very top */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button variant="contained" color="primary" onClick={() => setFlowchartDialogOpen(true)}>
            Generate Flowchart
          </Button>
        </Box>
        {/* Always show flowchart preview below the button */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          {flowchartImageUrl ? (
            <img src={flowchartImageUrl} alt="Last generated flowchart" style={{ maxWidth: '100%' }} />
          ) : (
            <FlowChartCanvas width={500} height={350} />
          )}
        </Box>
        {/* Main content below (overview, reports, settings, or detail) */}
        {selectedItem === 'overview' && (
          <Typography variant="body1" sx={{ mb: 2 }}>
            Use the button above to generate a flowchart for your documents.
          </Typography>
        )}
        {selectedItem === 'reports' && renderReportsAndAnalytics()}
        {selectedItem === 'settings' && renderSettings()}
        {selectedItem !== 'overview' && selectedItem !== 'reports' && selectedItem !== 'settings' && renderComponent(selectedItem)}
        {/* FlowchartBuilder Modal */}
        <Dialog open={flowchartDialogOpen} onClose={() => setFlowchartDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Generate Flowchart</DialogTitle>
          <DialogContent>
            <FlowchartBuilder 
              documentMapItems={mappedDocumentList} 
              onFlowchartGenerated={handleFlowchartGenerated}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFlowchartDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const drawerWidth = 240;

  const handleAddDocument = () => {
    setAddDialogOpen(true);
    setError('');
  };
  const handleDialogClose = () => {
    setAddDialogOpen(false);
    setError('');
  };
  const handleDialogSubmit = async (name) => {
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_BASE_URL}/document-map/create`, {
        title: name,
        projectId
      });
      setAddDialogOpen(false);
      // Refresh document map list after adding
      // fetchDocumentMapItems();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add document');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter and sort mappedDocumentList by search term and sort order
  const filteredDocumentMapItems = mappedDocumentList
    .filter(item => item.title.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.title.localeCompare(b.title);
      } else {
        return b.title.localeCompare(a.title);
      }
    });

  // Helper to determine if a document should navigate
  const handleListItemClick = (item) => {
    if (!projectId) return;
    switch (item.title.toLowerCase()) {
      case 'protocol':
        navigate(`/document-editor/protocol/${projectId}?projectId=${projectId}`);
        break;
      case 'sap':
        navigate(`/document-editor/sap/${projectId}?projectId=${projectId}`);
        break;
      case 'icf':
        navigate(`/document-editor/icf/${projectId}?projectId=${projectId}`);
        break;
      case 'clinical intake form':
        navigate(`/clinical-intake?projectId=${projectId}`);
        break;
      default:
        setSelectedItem(item._id);
    }
  };

  return (
    <Box sx={{ 
      height: '100%', 
      bgcolor: 'background.default', 
      display: 'flex', 
      flexDirection: 'column' 
    }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Document Map
          </Typography>
          <TextField
            size="small"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2, width: 250 }}
          />
          <Tooltip title="Filter">
            <IconButton onClick={e => setFilterMenuAnchorEl(e.currentTarget)}>
              <FilterIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={filterMenuAnchorEl}
            open={Boolean(filterMenuAnchorEl)}
            onClose={() => setFilterMenuAnchorEl(null)}
          >
            <MenuItem
              selected={sortOrder === 'asc'}
              onClick={() => { setSortOrder('asc'); setFilterMenuAnchorEl(null); }}
            >
              Sort A-Z
            </MenuItem>
            <MenuItem
              selected={sortOrder === 'desc'}
              onClick={() => { setSortOrder('desc'); setFilterMenuAnchorEl(null); }}
            >
              Sort Z-A
            </MenuItem>
          </Menu>
          {/* <Button
            startIcon={<AddIcon />}
            variant="contained"
            sx={{ ml: 2 }}
            onClick={handleAddDocument}
          >
            Add Document
          </Button> */}
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Main content area */}
        <Box sx={{ 
          flexGrow: 1, 
          overflow: 'hidden', 
          p: 0,
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {renderMainContent()}
        </Box>
        {/* Right sidebar navigation */}
        <Drawer
          variant="permanent"
          anchor="right"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              position: 'relative',
              border: 'none',
              borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
              height: 'calc(100vh - 64px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
            },
          }}
        >
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              <Typography variant="h6">Documents</Typography>
            </Box>
            <List>
              {filteredDocumentMapItems.map((item) => (
                <ListItem 
                  button 
                  key={item._id}
                  selected={selectedItem === item._id}
                  onClick={() => handleListItemClick(item)}
                  sx={{
                    borderLeft: selectedItem === item._id ? 3 : 0,
                    borderColor: 'primary.main',
                    '&.Mui-selected': {
                      bgcolor: 'rgba(25, 118, 210, 0.08)',
                    },
                    '&:hover': {
                      bgcolor: 'rgba(25, 118, 210, 0.04)',
                      cursor: 'pointer',
                    },
                  }}
                >
                  <ListItemIcon>
                    <AssignmentIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.title}
                    primaryTypographyProps={{
                      style: {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
      </Box>

      {/* Next Button for StudyFormStepper */}
      {onSubmit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNextClick}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Finish'}
          </Button>
        </Box>
      )}

      <AddDocumentDialog
        open={addDialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        loading={submitting}
        error={error}
      />
    </Box>
  );
};

export default DocumentMap; 